import React, { useEffect, useState } from 'react';
import { View, Text, Pressable, AppState, Animated, Easing, useWindowDimensions, Platform } from 'react-native';
import { BlurView } from 'expo-blur';
import { logout } from '../lib/auth';
import NetInfo from '@react-native-community/netinfo';
import { useAppStore } from '../state/appStore';
import { fetchActiveQuestionSequence, postResponse } from '../lib/api';
import { refreshAuth } from '../lib/auth';

type Props = { deviceId: string; onLogout?: () => void };

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export function MainKioskScreen({ deviceId, onLogout }: Props) {
  const { width, height } = useWindowDimensions();
  const uiFontFamily = Platform.select({ ios: 'Arial', android: 'sans-serif', web: 'Arial' });
  const {
    isOnline,
    setOnline,
    activeQuestions,
    currentQuestionIndex,
    setActiveQuestions,
    setCurrentQuestionIndex,
    resetQuestionSequence,
  } = useAppStore();
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showAck, setShowAck] = useState(false);
  const ackOpacity = React.useRef(new Animated.Value(0)).current;
  const ackScale = React.useRef(new Animated.Value(0.9)).current;
  const currentAssignmentIdRef = React.useRef<string | null>(null);
  const currentIndexRef = React.useRef(0);
  const activeQuestion = activeQuestions[currentQuestionIndex] || null;

  const playAck = () =>
    new Promise<void>((resolve) => {
      setShowAck(true);
      ackOpacity.setValue(0);
      ackScale.setValue(0.9);
      Animated.sequence([
        Animated.parallel([
          Animated.timing(ackOpacity, { toValue: 1, duration: 180, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
          Animated.spring(ackScale, { toValue: 1.05, useNativeDriver: true })
        ]),
        Animated.delay(500),
        Animated.parallel([
          Animated.timing(ackOpacity, { toValue: 0, duration: 200, easing: Easing.in(Easing.cubic), useNativeDriver: true }),
          Animated.timing(ackScale, { toValue: 0.98, duration: 200, useNativeDriver: true })
        ])
      ]).start(() => {
        setShowAck(false);
        resolve();
      });
    });

  useEffect(() => {
    currentAssignmentIdRef.current = activeQuestion?.assignmentId || null;
    currentIndexRef.current = currentQuestionIndex;
  }, [activeQuestion, currentQuestionIndex]);

  // Reset sequence on launch/device switch (chosen behavior: restart from first question).
  useEffect(() => {
    resetQuestionSequence();
    setLoadError(null);
    setSubmitError(null);
  }, [deviceId, resetQuestionSequence]);

  useEffect(() => {
    const subNet = NetInfo.addEventListener((state) => {
      console.log('[ui] net change isConnected=', state.isConnected);
      setOnline(Boolean(state.isConnected));
    });
    const subApp = AppState.addEventListener('change', (s) => {
      console.log('[ui] appstate=', s);
      if (s === 'active') refreshAuth();
    });
    return () => { subNet(); subApp.remove(); };
  }, [setOnline]);

  useEffect(() => {
    let timer: any;
    const load = async () => {
      setLoadError(null);
      setLoading(true);
      setSubmitError(null);
      console.log('[ui] load active question for deviceId=', deviceId);
      try {
        const sequence = await fetchActiveQuestionSequence(deviceId);
        console.log('[ui] activeQuestions count=', sequence.length);
        setActiveQuestions(sequence);

        if (sequence.length === 0) {
          setCurrentQuestionIndex(0);
          return;
        }

        const currentAssignmentId = currentAssignmentIdRef.current;
        if (!currentAssignmentId) {
          setCurrentQuestionIndex(0);
          return;
        }

        const nextIndex = sequence.findIndex((q) => q.assignmentId === currentAssignmentId);
        if (nextIndex >= 0) {
          setCurrentQuestionIndex(nextIndex);
        } else {
          setCurrentQuestionIndex(Math.min(currentIndexRef.current, sequence.length - 1));
        }
      } catch (e: any) {
        console.warn('[ui] failed to load active question', e);
        setActiveQuestions([]);
        setCurrentQuestionIndex(0);
        setLoadError(e?.message || 'Failed to load question');
      } finally {
        setLoading(false);
      }
    };
    load();
    timer = setInterval(load, Number(process.env.EXPO_PUBLIC_POLL_INTERVAL_MS || 15000));
    return () => clearInterval(timer);
  }, [deviceId, setActiveQuestions, setCurrentQuestionIndex]);

  if (loading) return <Text>Loading…</Text>;
  if (loadError) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24 }}>
        <Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 8, fontFamily: uiFontFamily }}>
          Could not load the active question
        </Text>
        <Text style={{ color: '#555', textAlign: 'center', marginBottom: 16, fontFamily: uiFontFamily }}>{loadError}</Text>
        <Pressable
          onPress={() => { onLogout?.(); logout().catch(() => {}); }}
          style={{ paddingVertical: 12, paddingHorizontal: 18, borderRadius: 10, backgroundColor: '#ef4444' }}
        >
          <Text style={{ color: 'white', fontWeight: '700', fontFamily: uiFontFamily }}>Log out</Text>
        </Pressable>
      </View>
    );
  }
  if (!activeQuestion) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24 }}>
        <Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 8, fontFamily: uiFontFamily }}>No active question</Text>
        <Text style={{ color: '#555', textAlign: 'center', marginBottom: 16, fontFamily: uiFontFamily }}>
          This device has no currently-active assignment. Please add an assignment in the admin UI.
        </Text>
        <Pressable
          onPress={() => { onLogout?.(); logout().catch(() => {}); }}
          style={{ paddingVertical: 12, paddingHorizontal: 18, borderRadius: 10, backgroundColor: '#ef4444' }}
        >
          <Text style={{ color: 'white', fontWeight: '700', fontFamily: uiFontFamily }}>Log out</Text>
        </Pressable>
      </View>
    );
  }

  // --- Responsive layout ---
  const optionCount = Math.max(1, activeQuestion.options?.length || 1);

  // Horizontal padding scales with screen size (zoom-y feel).
  const screenPadX = clamp(Math.round(width * 0.04), 12, 44);
  const gap = clamp(Math.round(width * 0.025), 10, 44);
  const availableWidth = Math.max(1, width - screenPadX * 2);
  const slotWidth = (availableWidth - gap * (optionCount - 1)) / optionCount;

  // Emoji size chosen so all options fit on ONE row.
  // Prefer using vertical space too, so large screens "zoom in" naturally.
  const maxEmojiByHeight = Math.floor(height * 0.26);
  const emojiSize = clamp(Math.floor(Math.min(slotWidth * 0.92, maxEmojiByHeight)), 36, 240);

  // Question sizing: wrap to <=3 lines, shrink slightly for long questions.
  const qLen = (activeQuestion.text || '').length;
  // Base font size (doubled per request), still capped for small screens.
  const baseQuestionSize = clamp(Math.round(Math.min(width * 0.09, height * 0.125)), 32, 102);
  const shrinkFactor = clamp(1 - Math.max(0, qLen - 70) / 220, 0.72, 1);
  const questionFontSize = Math.round(baseQuestionSize * shrinkFactor);
  const questionLineHeight = Math.round(questionFontSize * 1.15);
  const questionToEmojisGap = clamp(Math.round(height * 0.035), 22, 64);
  const questionCount = activeQuestions.length;
  const useCompactDots = questionCount > 10;
  const dotsToRender = useCompactDots ? 3 : questionCount;

  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: screenPadX }}>
      <Pressable
        onPress={() => { onLogout?.(); logout().catch(() => {}); }}
        style={{ position: 'absolute', top: 8, right: 8, paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 }}
        hitSlop={{ top: 24, bottom: 24, left: 24, right: 24 }}
        pressRetentionOffset={{ top: 12, bottom: 12, left: 12, right: 12 }}
      >
        <Text style={{ color: '#888', fontSize: 16, fontFamily: uiFontFamily }}>Logout</Text>
      </Pressable>
      <Text
        numberOfLines={3}
        ellipsizeMode="tail"
        style={{
          fontSize: questionFontSize,
          lineHeight: questionLineHeight,
          marginBottom: questionToEmojisGap,
          textAlign: 'center',
          paddingHorizontal: 4,
          fontFamily: uiFontFamily,
        }}
      >
        {activeQuestion.text}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
        {activeQuestion.options.map((opt, idx) => (
          <Pressable
            key={idx}
            disabled={!isOnline || busy}
          onPress={async () => {
            setBusy(true);
            setSubmitError(null);
            try {
              await postResponse(deviceId, activeQuestion.id, idx);
              await playAck();
              if (activeQuestions.length > 1) {
                setCurrentQuestionIndex((currentQuestionIndex + 1) % activeQuestions.length);
              } else {
                setCurrentQuestionIndex(0);
              }
            } catch (e: any) {
              console.warn('[ui] failed to post response', e);
              setSubmitError(e?.message || 'Failed to save response. Please try again.');
            } finally { setBusy(false); }
            }}
            style={({ pressed }) => ({
              width: slotWidth,
              alignItems: 'center',
              justifyContent: 'center',
              paddingVertical: 4,
              marginVertical: 8,
              marginRight: idx === optionCount - 1 ? 0 : gap,
              backgroundColor: 'transparent',
              transform: [{ scale: pressed ? 0.96 : 1 }],
            })}
            hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
          >
            <Text style={{ fontSize: emojiSize }}>{opt.emoji}</Text>
          </Pressable>
        ))}
      </View>
      {showAck && (
        <Animated.View
          pointerEvents="none"
          style={{
            position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
            opacity: ackOpacity
          }}
        >
          <BlurView intensity={40} tint="light" style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }} />
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Animated.View style={{
              transform: [{ scale: ackScale }],
              backgroundColor: '#10b981',
              paddingVertical: 22,
              paddingHorizontal: 32,
              borderRadius: 9999,
              shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 16, shadowOffset: { width: 0, height: 8 }
            }}>
              <Text style={{ fontSize: 88, color: 'white' }}>✓</Text>
            </Animated.View>
            <Text style={{ marginTop: 16, fontSize: 28, fontWeight: '600', color: '#065f46', fontFamily: uiFontFamily }}>
              Thank you!
            </Text>
          </View>
        </Animated.View>
      )}
      {submitError && (
        <Text style={{ marginTop: 16, color: '#b91c1c', textAlign: 'center', maxWidth: 680, fontFamily: uiFontFamily }}>
          {submitError}
        </Text>
      )}
      {questionCount > 1 && (
        <View
          pointerEvents="none"
          style={{
            position: 'absolute',
            right: 24,
            bottom: 24,
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {Array.from({ length: dotsToRender }).map((_, idx) => {
            const backgroundColor = useCompactDots
              ? '#111111'
              : idx === currentQuestionIndex
                ? '#374151'
                : '#9ca3af';
            return (
              <View
                key={idx}
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 9999,
                  backgroundColor,
                  marginLeft: idx === 0 ? 0 : 10,
                }}
              />
            );
          })}
        </View>
      )}
      {!isOnline && <Text style={{ marginTop: 24, color: 'red', fontFamily: uiFontFamily }}>Offline — taps disabled</Text>}
    </View>
  );
}
